import {
  DiameterDisplayUnit,
  DISPLAY_UNIT_DEFAULTS,
  DISPLAY_UNIT_LABELS,
  DISPLAY_UNIT_OPTIONS,
  DisplayUnitMetric,
  DisplayUnits,
  FlowDisplayUnit,
  HeadDisplayUnit,
  NpshrDisplayUnit,
  PowerDisplayUnit,
} from "./definitions";
import {
  convertViscosityFromCanonical as convertViscosityFromCanonicalBase,
  convertViscosityToCanonical as convertViscosityToCanonicalBase,
  normalizeViscosityUnit,
} from "../viscosity/converters";
import { ViscosityDisplayUnit } from "../viscosity/definitions";

type NumberValue = number | null | undefined;
type NumberValueOrList = NumberValue | ReadonlyArray<NumberValue>;

type ScaleMap = Record<string, number>;

const FLOW_SCALE_MAP: ScaleMap = {
  "m3/h": 1,
  "m3/min": 1 / 60,
  "L/s": 1000 / 3600,
  "L/min": 1000 / 60,
  gpm: 4.4028675393,
};

const HEAD_SCALE_MAP: ScaleMap = {
  m: 1,
  ft: 3.280839895,
};

const POWER_SCALE_MAP: ScaleMap = {
  kW: 1,
  hp: 1.34102209,
};

const NPSHR_SCALE_MAP: ScaleMap = {
  m: 1,
  ft: 3.280839895,
};

const DIAMETER_SCALE_MAP: ScaleMap = {
  mm: 1,
  in: 1 / 25.4,
};

const mapNumberValue = <T extends NumberValueOrList>(value: T, scale: number): T => {
  if (Array.isArray(value)) {
    return value.map((item) => mapNumberValue(item, scale)) as unknown as T;
  }

  if (value === null || value === undefined) {
    return value;
  }

  return Number(((value as number) * scale).toFixed(12)) as T;
};

const convertWithScale = <T extends NumberValueOrList>(
  value: T,
  unit: string,
  scaleMap: ScaleMap
): T => mapNumberValue(value, scaleMap[unit] ?? 1);

const convertToCanonicalWithScale = <T extends NumberValueOrList>(
  value: T,
  unit: string,
  scaleMap: ScaleMap
): T => mapNumberValue(value, 1 / (scaleMap[unit] ?? 1));

const isValidUnit = <T extends readonly string[]>(value: string | undefined, options: T): value is T[number] => {
  return !!value && options.includes(value as T[number]);
};

export const normalizeDisplayUnits = (
  raw?: Partial<Record<DisplayUnitMetric, string | null | undefined>> | null
): DisplayUnits => {
  const flow = raw?.flow ?? undefined;
  const head = raw?.head ?? undefined;
  const power = raw?.power ?? undefined;
  const npshr = raw?.npshr ?? undefined;
  const diameter = raw?.diameter ?? undefined;
  const viscosity = raw?.viscosity ?? undefined;

  return {
    flow: isValidUnit(flow, DISPLAY_UNIT_OPTIONS.flow)
      ? flow
      : DISPLAY_UNIT_DEFAULTS.flow,
    head: isValidUnit(head, DISPLAY_UNIT_OPTIONS.head)
      ? head
      : DISPLAY_UNIT_DEFAULTS.head,
    power: isValidUnit(power, DISPLAY_UNIT_OPTIONS.power)
      ? power
      : DISPLAY_UNIT_DEFAULTS.power,
    npshr: isValidUnit(npshr, DISPLAY_UNIT_OPTIONS.npshr)
      ? npshr
      : DISPLAY_UNIT_DEFAULTS.npshr,
    diameter: isValidUnit(diameter, DISPLAY_UNIT_OPTIONS.diameter)
      ? diameter
      : DISPLAY_UNIT_DEFAULTS.diameter,
    viscosity: isValidUnit(viscosity, DISPLAY_UNIT_OPTIONS.viscosity)
      ? viscosity
      : DISPLAY_UNIT_DEFAULTS.viscosity,
    efficiency: DISPLAY_UNIT_DEFAULTS.efficiency,
  };
};

export const convertFlowFromCanonical = <T extends NumberValueOrList>(
  value: T,
  unit: FlowDisplayUnit
): T => convertWithScale(value, unit, FLOW_SCALE_MAP);

export const convertFlowToCanonical = <T extends NumberValueOrList>(
  value: T,
  unit: FlowDisplayUnit
): T => convertToCanonicalWithScale(value, unit, FLOW_SCALE_MAP);

export const convertHeadFromCanonical = <T extends NumberValueOrList>(
  value: T,
  unit: HeadDisplayUnit
): T => convertWithScale(value, unit, HEAD_SCALE_MAP);

export const convertHeadToCanonical = <T extends NumberValueOrList>(
  value: T,
  unit: HeadDisplayUnit
): T => convertToCanonicalWithScale(value, unit, HEAD_SCALE_MAP);

export const convertPowerFromCanonical = <T extends NumberValueOrList>(
  value: T,
  unit: PowerDisplayUnit
): T => convertWithScale(value, unit, POWER_SCALE_MAP);

export const convertNpshrFromCanonical = <T extends NumberValueOrList>(
  value: T,
  unit: NpshrDisplayUnit
): T => convertWithScale(value, unit, NPSHR_SCALE_MAP);

export const convertDiameterFromCanonical = <T extends NumberValueOrList>(
  value: T,
  unit: DiameterDisplayUnit
): T => convertWithScale(value, unit, DIAMETER_SCALE_MAP);

export const convertDiameterToCanonical = <T extends NumberValueOrList>(
  value: T,
  unit: DiameterDisplayUnit
): T => convertToCanonicalWithScale(value, unit, DIAMETER_SCALE_MAP);

export const convertViscosityFromCanonical = (
  value?: number | null,
  unit?: string | null,
  sg?: number | null
) =>
  convertViscosityFromCanonicalBase(
    value,
    normalizeViscosityUnit(unit) as ViscosityDisplayUnit,
    sg
  );

export const convertViscosityToCanonical = (
  value?: number | null,
  unit?: string | null,
  sg?: number | null
) =>
  convertViscosityToCanonicalBase(
    value,
    normalizeViscosityUnit(unit) as ViscosityDisplayUnit,
    sg
  );

export const getDisplayUnitLabel = (
  metric: DisplayUnitMetric,
  unit: DisplayUnits[DisplayUnitMetric]
): string => DISPLAY_UNIT_LABELS[metric][unit as never] as string;

export const formatAxisLabel = (
  metric: DisplayUnitMetric,
  unit: DisplayUnits[DisplayUnitMetric]
): string => {
  const label = getDisplayUnitLabel(metric, unit);

  switch (metric) {
    case "flow":
      return label;
    case "head":
      return `H(${label})`;
    case "power":
      return `P(${label})`;
    case "npshr":
      return `NPSHr(${label})`;
    case "diameter":
      return label;
    case "viscosity":
      return label;
    case "efficiency":
      return `EFF(${label})`;
    default:
      return label;
  }
};
